import { useEffect, useState } from "react";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { Plus, X, Calculator, Trash2, Pencil, FileText, CalendarPlus } from "lucide-react";
import { PrestationPlanningDialog } from "../components/PrestationPlanning";
import { useNavigate } from "react-router-dom";
import { DateInput } from "../components/DateInput";
import { SearchSelect } from "../components/SearchSelect";

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

function fmtEUR(n) { return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(Number(n || 0)); }

export default function Prestations() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [editing, setEditing] = useState(null);
  const [company, setCompany] = useState(null);
  const [toPlan, setToPlan] = useState(null);
  const { lastEvent } = useRealtime();
  const nav = useNavigate();

  const load = async () => {
    const [p, s, c] = await Promise.all([http.get("/prestations"), http.get("/sites"), http.get("/settings/company")]);
    setItems(p.data); setSites(s.data); setCompany(c.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lastEvent?.event === "prestation_changed" || lastEvent?.event === "site_changed") load(); }, [lastEvent]);

  const openNew = () => setEditing({
    name: "",
    site_id: sites[0]?.id || "",
    client_name: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    days_of_week: [0, 1, 2, 3, 4],
    start_time: "08:00",
    end_time: "20:00",
    agents_count: 1,
    hourly_rate_ht: company?.default_hourly_rate_ht ?? 18,
    night_surcharge_pct: company?.default_night_surcharge_pct ?? 10,
    sunday_surcharge_pct: company?.default_sunday_surcharge_pct ?? 10,
    holiday_surcharge_pct: company?.default_holiday_surcharge_pct ?? 100,
    vat_rate: company?.default_vat_rate ?? 20,
    notes: "",
  });

  const makeDevis = async (p) => {
    const items = p.simulation.breakdown.filter((b) => b.hours > 0).map((b) => ({
      description: `${p.name} — ${b.label}`, quantity: b.hours, unit: "h", unit_price_ht: b.rate, vat_rate: p.vat_rate,
    }));
    if (items.length === 0) return alert("Aucune heure à facturer");
    const r = await http.post("/devis", {
      client_name: p.client_name || "Client",
      date: new Date().toISOString().slice(0, 10),
      valid_until: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
      items,
      notes: `Devis généré à partir de la prestation "${p.name}"`,
    });
    await http.post(`/prestations/${p.id}/devis-link`, { devis_id: r.data.id });
    nav("/devis");
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="prestations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Prestations</h1>
          <p className="text-sm text-slate-600">Simulez le coût d'une mission avant de faire un devis.</p>
        </div>
        <button data-testid="add-prestation" onClick={openNew} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md text-sm font-semibold"><Plus className="h-4 w-4"/> Nouvelle simulation</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((p) => (
          <div key={p.id} className="border border-slate-200 bg-white rounded-md p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="font-display text-lg font-bold truncate">{p.name}</div>
                <div className="text-xs text-slate-500">{p.client_name || "—"} · {p.start_date} → {p.end_date}</div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`edit-prestation-${p.id}`} onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-slate-100"><Pencil className="h-4 w-4"/></button>
                <button onClick={async()=>{ if(askDelete("Supprimer ?")){ await http.delete(`/prestations/${p.id}`); load();}}} title={delTitle()} className="p-1.5 rounded hover:bg-rose-50"><Trash2 className="h-4 w-4 text-rose-600"/></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              {p.simulation?.breakdown?.filter(b => b.hours > 0).map((b, i) => (
                <div key={i} className="flex justify-between border-l-4 border-slate-200 pl-2">
                  <span className="text-slate-600">{b.label}</span>
                  <span className="font-mono font-semibold">{b.hours}h × {fmtEUR(b.rate)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 pt-3 grid grid-cols-3 gap-2 text-xs">
              <div><div className="text-slate-500">Total HT</div><div className="font-display text-base font-bold">{fmtEUR(p.simulation?.total_ht)}</div></div>
              <div><div className="text-slate-500">TVA {p.vat_rate}%</div><div className="font-display text-base font-bold">{fmtEUR(p.simulation?.vat)}</div></div>
              <div><div className="text-slate-500">Total TTC</div><div className="font-display text-base font-extrabold text-sky-700">{fmtEUR(p.simulation?.total_ttc)}</div></div>
            </div>
            <button data-testid={`prestation-to-devis-${p.id}`} onClick={() => makeDevis(p)} className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 rounded-md py-2 text-xs font-semibold"><FileText className="h-3.5 w-3.5"/> Générer un devis</button>
            {p.devis && <div className="mt-2 text-[11px] text-slate-500" data-testid={`prestation-devis-status-${p.id}`}>Devis {p.devis.number} · <b className={p.devis.status === "accepted" ? "text-emerald-700" : "text-amber-700"}>{p.devis.status === "accepted" ? "accepté" : "en attente d'acceptation"}</b></div>}
            <button data-testid={`prestation-to-planning-${p.id}`} disabled={p.devis?.status !== "accepted"} onClick={() => setToPlan(p)}
              title={p.devis?.status === "accepted" ? "Créer les vacations à pourvoir dans le planning" : "Disponible quand le devis est accepté ou signé"}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md py-2 text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"><CalendarPlus className="h-3.5 w-3.5"/> Créer le planning</button>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-10 text-sm border border-dashed border-slate-200 rounded-md">Aucune prestation simulée.</div>}
      </div>

      {toPlan && <PrestationPlanningDialog prestation={toPlan} onClose={() => setToPlan(null)} onDone={load} />}
      {editing && <PrestaModal value={editing} onClose={()=>setEditing(null)} onSaved={load} sites={sites}/>}
    </div>
  );
}

function PrestaModal({ value, onClose, onSaved, sites }) {
  const [v, setV] = useState(value);
  const [sim, setSim] = useState(value.simulation || null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => {
    const has = v.days_of_week.includes(d);
    setV({ ...v, days_of_week: has ? v.days_of_week.filter(x => x !== d) : [...v.days_of_week, d].sort() });
  };

  const runSim = async () => {
    setErr("");
    try {
      const { data } = await http.post("/prestations/simulate", v);
      setSim(data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      if (v.id) await http.put(`/prestations/${v.id}`, v);
      else await http.post("/prestations", v);
      onSaved(); onClose();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[95vh] overflow-auto border border-slate-200 animate-fade-in-up" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-display text-xl font-bold">{v.id ? "Modifier" : "Nouvelle"} simulation</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <L label="Nom *"><input data-testid="prestation-name" className="input" value={v.name} onChange={(e)=>setV({...v, name:e.target.value})}/></L>
          <L label="Client"><input className="input" value={v.client_name} onChange={(e)=>setV({...v, client_name:e.target.value})}/></L>
          <L label="Site">
            <SearchSelect data-testid="prestations-site" className="input" value={v.site_id || ""} onChange={(e)=>setV({...v, site_id:e.target.value})}>
              <option value="">—</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SearchSelect>
          </L>
          <L label="Nb agents"><input type="number" min={1} className="input" value={v.agents_count} onChange={(e)=>setV({...v, agents_count:parseInt(e.target.value||1)})}/></L>
          <L label="Début *"><DateInput data-testid="prest-start-date" className="input" value={v.start_date} onChange={(e)=>setV({...v, start_date:e.target.value})}/></L>
          <L label="Fin *"><DateInput data-testid="prest-end-date" className="input" value={v.end_date} onChange={(e)=>setV({...v, end_date:e.target.value})}/></L>
          <L label="Heure début"><input type="time" className="input" value={v.start_time} onChange={(e)=>setV({...v, start_time:e.target.value})}/></L>
          <L label="Heure fin"><input type="time" className="input" value={v.end_time} onChange={(e)=>setV({...v, end_time:e.target.value})}/></L>
          <div className="md:col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Jours de la semaine</div>
            <div className="flex gap-1">
              {ALL_DAYS.map((d) => (
                <button key={d} type="button" onClick={()=>toggleDay(d)}
                  className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-colors ${v.days_of_week.includes(d) ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {DAY_LABELS[d]}
                </button>
              ))}
            </div>
          </div>
          <L label="Tarif horaire HT (€)"><input type="number" step="0.01" className="input" value={v.hourly_rate_ht} onChange={(e)=>setV({...v, hourly_rate_ht:parseFloat(e.target.value||0)})}/></L>
          <L label="TVA (%)"><input type="number" step="0.1" className="input" value={v.vat_rate} onChange={(e)=>setV({...v, vat_rate:parseFloat(e.target.value||0)})}/></L>
          <L label="Majoration nuit 21h-06h (%)"><input type="number" step="0.1" className="input" value={v.night_surcharge_pct} onChange={(e)=>setV({...v, night_surcharge_pct:parseFloat(e.target.value||0)})}/></L>
          <L label="Majoration dimanche (%)"><input type="number" step="0.1" className="input" value={v.sunday_surcharge_pct} onChange={(e)=>setV({...v, sunday_surcharge_pct:parseFloat(e.target.value||0)})}/></L>
          <L label="Majoration fériés (%)"><input type="number" step="0.1" className="input" value={v.holiday_surcharge_pct} onChange={(e)=>setV({...v, holiday_surcharge_pct:parseFloat(e.target.value||0)})}/></L>
          <L label="Notes"><input className="input" value={v.notes || ""} onChange={(e)=>setV({...v, notes:e.target.value})}/></L>
        </div>

        {sim && (
          <div className="mx-5 mb-5 border border-sky-200 bg-sky-50/50 rounded-md p-4">
            <div className="flex items-center gap-2 mb-3"><Calculator className="h-4 w-4 text-sky-700"/><span className="font-display font-bold text-sky-900">Simulation</span></div>
            <div className="text-xs mb-3">{sim.days_count} jour(s) · {sim.agents_count} agent(s) · {sim.total_hours}h au total</div>
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-500 uppercase tracking-widest"><th className="pb-1 font-bold">Type</th><th className="pb-1 font-bold">Heures</th><th className="pb-1 font-bold">Tarif</th><th className="pb-1 font-bold text-right">Total HT</th></tr></thead>
              <tbody className="divide-y divide-sky-100">
                {sim.breakdown.map((b, i) => (
                  <tr key={i}><td className="py-1.5">{b.label}</td><td className="font-mono">{b.hours}</td><td className="font-mono">{fmtEUR(b.rate)}</td><td className="text-right font-mono font-semibold">{fmtEUR(b.total)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="border-t border-sky-200 mt-3 pt-3 flex justify-end gap-6 text-sm">
              <div>Total HT: <span className="font-bold">{fmtEUR(sim.total_ht)}</span></div>
              <div>TVA {sim.vat_rate}%: <span className="font-bold">{fmtEUR(sim.vat)}</span></div>
              <div className="text-sky-800 font-display text-lg">TTC: <span className="font-extrabold">{fmtEUR(sim.total_ttc)}</span></div>
            </div>
          </div>
        )}
        {err && <div className="mx-5 mb-5 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="prestation-simulate" onClick={runSim} className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-md inline-flex items-center gap-1"><Calculator className="h-4 w-4"/>Simuler</button>
          <button data-testid="prestation-save" disabled={saving} onClick={save} className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md disabled:opacity-70">{saving?"…":"Enregistrer"}</button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}
function L({label, children}) { return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>; }
